import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type WorkAgreementsAccessJSON = z.infer<typeof WorkAgreementsAccessSchema.WorkAgreementsAccessJSONZ>;

export type WorkAgreementsAccessParams = z.infer<typeof WorkAgreementsAccessSchema.WorkAgreementsAccessZ>;

export default class WorkAgreementsAccessSchema {
  public static readonly WorkAgreementsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.WorkAgreementsAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly WorkAgreementsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.WorkAgreementsAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): WorkAgreementsAccessParams {
    return parseOrThrow(WorkAgreementsAccessSchema.WorkAgreementsAccessZ, values, {
      logError: true,
      schemaName: "WorkAgreementsAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): WorkAgreementsAccessJSON {
    return parseOrThrow(WorkAgreementsAccessSchema.WorkAgreementsAccessJSONZ, values, {
      logError: true,
      schemaName: "WorkAgreementsAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is WorkAgreementsAccessParams {
    try {
      WorkAgreementsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is WorkAgreementsAccessJSON {
    try {
      WorkAgreementsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: WorkAgreementsAccessJSON): WorkAgreementsAccessParams {
    const parsed = parseOrThrow(WorkAgreementsAccessSchema.WorkAgreementsAccessJSONZ, data, {
      logError: true,
      schemaName: "WorkAgreementsAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return WorkAgreementsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: WorkAgreementsAccessParams): WorkAgreementsAccessJSON {
    const parsed = WorkAgreementsAccessSchema.validate(notification);
    return WorkAgreementsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    WorkAgreementsAccessSchema.WorkAgreementsAccessJSONZ,
    WorkAgreementsAccessSchema.WorkAgreementsAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.WorkAgreementsAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.WorkAgreementsAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
